import { BusinessObjectBase } from './base';
import { measurementTypeSchema } from '@schemas/business-objects/measurement-type';
import type { Measurement } from './measurement';
import type { Id } from '../base-types';

export interface MeasurementTypeData {
  readonly id: Id;
  readonly name: string;
  readonly sequence?: number;
  readonly isScalingReference?: boolean;
  readonly referenceLength?: number;
  readonly referenceLengthOptions?: string;
  readonly description?: string;
  readonly measurements?: Measurement[];
}

export interface MeasurementType extends Readonly<MeasurementTypeData> {}
export class MeasurementType extends BusinessObjectBase {
  static readonly entityName = 'MeasurementType' as const;
  static readonly plural = 'measurement-types';
  static schema = measurementTypeSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<MeasurementType[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new MeasurementType(data));
  }

  static async findOne(filter?: any): Promise<MeasurementType | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new MeasurementType(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<MeasurementType> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new MeasurementType(data);
  }

  static async create(
    data: Omit<MeasurementTypeData, 'id'>,
  ): Promise<MeasurementType> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new MeasurementType(result);
  }

  static async createMany(
    data: Omit<MeasurementTypeData, 'id'>[],
  ): Promise<MeasurementType[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new MeasurementType(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<MeasurementTypeData>,
  ): Promise<MeasurementType> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new MeasurementType(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  async makeScalingReference(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${MeasurementType.plural}/${(this as any).id}/make-scaling-reference`;
    return BusinessObjectBase.post<any>(url, {});
  }
}
